import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IGrowerAnalysisResponse, ISalesRepAnalysisResponse, ISellerAnalysisResponse, ITerritoryAnalysisResponse } from './InsightsModels';
import { getAiExperience, getGrowerAnalysis, getSalesRepAnalyses, getSalesRepAnalysis, getSellerAnalysis, getSellerMemberAnalysis, getTerritoryAnalyses, getTerritoryAnalysis, sendFeedbackForConcern } from './InsightThunks';

/** Insights is also known as Root Cause */

export type AiExperience = 'Unknown' | 'None' | 'MultiSalesRep' | 'SalesRep' | 'Seller' | 'Grower';

export interface IInsightsState
{
	isError: boolean;
	errorMessage: string | undefined;
	currentTerritoryId: string | undefined;
	currentSalesRepId: string | undefined;
	currentSellerId: string | undefined;
	currentGrowerId: string | undefined;

	isLoadingAnalyses: boolean;
	territoryAnalyses: ITerritoryAnalysisResponse[];
	singleTerritoryAnalysis: ITerritoryAnalysisResponse | null;
	multiSalesRepAnalyses: ISalesRepAnalysisResponse[];
	singleSalesRepAnalysis: ISalesRepAnalysisResponse | null;
	sellerMemberAnalyses: ISellerAnalysisResponse[];
	singleSellerAnalysis: ISellerAnalysisResponse | null;
	singleGrowerAnalysis: IGrowerAnalysisResponse | null;
	
	isLoadingExperience: boolean;
	aiExperience: AiExperience | null;

	/** Why the territory lookup failed, when it did. Does not set isError -- see the reducer. */
	territoryErrorMessage: string | undefined;

	isLoadingFeedback: boolean;
}

export const initialState: IInsightsState = {
	isError: false,	
	errorMessage: undefined,
	currentTerritoryId: undefined,
	currentSalesRepId: undefined,
	currentSellerId: undefined,
	currentGrowerId: undefined,

	isLoadingAnalyses: false,
	territoryAnalyses: [],
	singleTerritoryAnalysis: null,
	multiSalesRepAnalyses: [],
	singleSalesRepAnalysis: null,
	sellerMemberAnalyses: [],
	singleSellerAnalysis: null,
	singleGrowerAnalysis: null,

	aiExperience: null,
	isLoadingExperience: false,
	territoryErrorMessage: undefined,

	isLoadingFeedback: false,
};

export const insightsSlice = createSlice({
	name: 'insights',
	initialState,
	reducers: {
		clearState: (state) => 
		{
			state.isError = false;
			state.errorMessage = undefined;
			state.isLoadingAnalyses = false;
			state.isLoadingExperience = false;

			state.currentGrowerId = undefined;
			state.currentSellerId = undefined;
			state.currentSalesRepId = undefined;
			state.currentTerritoryId = undefined;

			state.territoryAnalyses = [];
			state.singleTerritoryAnalysis = null;
			state.multiSalesRepAnalyses = [];
			state.singleSalesRepAnalysis = null;
			state.sellerMemberAnalyses = [];
			state.singleSellerAnalysis = null;
			state.singleGrowerAnalysis = null;

			state.aiExperience = null;

			return state;
		},
		clearError: (state) =>
		{
			state.isError = false;
			state.errorMessage = undefined;

			return state;
		},
		clearSelectedTerritoryAnalysis: (state) =>
		{
			state.currentTerritoryId = undefined;
			state.singleTerritoryAnalysis = null;

			return state;
		},
		clearSelectedSalesRepAnalysis: (state) =>
		{
			state.currentSalesRepId = undefined;
			state.singleSalesRepAnalysis = null;

			return state;
		},
		clearSelectedSellerAnalysis: (state) =>
		{
			state.currentSellerId = undefined;
			state.singleSellerAnalysis = null;

			return state;
		},
		clearSelectedGrowerAnalysis: (state) =>
		{
			state.currentGrowerId = undefined;
			state.singleGrowerAnalysis = null;

			return state;
		},
		setSelectedTerritoryId: (state, { payload } : PayloadAction<string>) =>
		{
			state.currentTerritoryId = payload;
			return state;
		},
		setSelectedSalesRepId: (state, { payload } : PayloadAction<string>) =>
		{
			state.currentSalesRepId = payload;
			return state;
		},
		setSelectedSellerId: (state, { payload } : PayloadAction<string>) =>
		{
			state.currentSellerId = payload;
			return state;
		},
		setSelectedGrowerId: (state, { payload } : PayloadAction<string>) =>
		{
			state.currentGrowerId = payload;
			return state;
		},
	},
	extraReducers: (builder) =>
	{
		// AI Experience
		builder.addCase(getAiExperience.pending, (state, action) =>
		{
			state.isLoadingExperience = true;		
		});
		builder.addCase(getAiExperience.fulfilled, (state, action) =>
		{
			state.isLoadingExperience = false;
			state.aiExperience = action.payload as AiExperience;
		});
		builder.addCase(getAiExperience.rejected, (state, action) =>
		{
			state.isLoadingExperience = false;
			state.isError = true;
			state.aiExperience = 'Unknown';

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the AI User Experience type. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the AI User Experience type. Please refresh and try again.';
			}

		});

		// Territory Analyses (list for the current user)
		builder.addCase(getTerritoryAnalyses.pending, (state, action) =>
		{
			state.isLoadingAnalyses = true;
		});
		builder.addCase(getTerritoryAnalyses.fulfilled, (state, action) =>
		{
			state.isLoadingAnalyses = false;
			state.territoryAnalyses = action.payload as ITerritoryAnalysisResponse[];
		});
		builder.addCase(getTerritoryAnalyses.rejected, (state, action) =>
		{
			state.isLoadingAnalyses = false;
			// A user with no territory is an expected case and the view falls back to the sales rep
			// list, so this does not raise isError -- but the reason is kept for diagnosis.
			state.territoryAnalyses = [];
			state.territoryErrorMessage = (action.payload as string) ?? 'Unknown error loading territories.';
		});

		// Single Territory Analysis
		builder.addCase(getTerritoryAnalysis.pending, (state, action) =>
		{
			state.isLoadingAnalyses = true;
		});
		builder.addCase(getTerritoryAnalysis.fulfilled, (state, action) =>
		{
			state.isLoadingAnalyses = false;
			state.singleTerritoryAnalysis = action.payload as ITerritoryAnalysisResponse;
		});
		builder.addCase(getTerritoryAnalysis.rejected, (state, action) =>
		{
			state.isLoadingAnalyses = false;
			state.isError = true;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the Territory Analysis. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the Territory Analysis. Please refresh and try again.';
			}

		});

		// Multi Sales Rep Analyses
		builder.addCase(getSalesRepAnalyses.pending, (state, action) =>
		{
			state.isLoadingAnalyses = true;
		});
		builder.addCase(getSalesRepAnalyses.fulfilled, (state, action) =>
		{
			state.isLoadingAnalyses = false;
			state.multiSalesRepAnalyses = action.payload as ISalesRepAnalysisResponse[];
		});
		builder.addCase(getSalesRepAnalyses.rejected, (state, action) =>
		{
			state.isLoadingAnalyses = false;
			state.isError = true;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the Sales Rep Analyses. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the Sales Rep Analyses. Please refresh and try again.';
			}

		});


		// Single Sales Rep Analyses
		builder.addCase(getSalesRepAnalysis.pending, (state, action) =>
		{
			state.isLoadingAnalyses = true;
		});
		builder.addCase(getSalesRepAnalysis.fulfilled, (state, action) =>
		{
			state.isLoadingAnalyses = false;
			state.singleSalesRepAnalysis = action.payload as ISalesRepAnalysisResponse;
		});
		builder.addCase(getSalesRepAnalysis.rejected, (state, action) =>
		{
			state.isLoadingAnalyses = false;
			state.isError = true;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the Sales Rep Analysis. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the Sales Rep Analysis. Please refresh and try again.';
			}

		});

		// Seller members Analyses
		builder.addCase(getSellerMemberAnalysis.pending, (state, action) =>
		{
			state.isLoadingAnalyses = true;
		});
		builder.addCase(getSellerMemberAnalysis.fulfilled, (state, action) =>
		{
			state.isLoadingAnalyses = false;
			state.sellerMemberAnalyses = action.payload as ISellerAnalysisResponse[];
		});
		builder.addCase(getSellerMemberAnalysis.rejected, (state, action) =>
		{
			state.isLoadingAnalyses = false;
			state.isError = true;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the Seller Member Analyses. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the Seller Member Analyses. Please refresh and try again.';
			}

		});

		// Single Seller Analyses
		builder.addCase(getSellerAnalysis.pending, (state, action) =>
		{
			state.isLoadingAnalyses = true;
		});
		builder.addCase(getSellerAnalysis.fulfilled, (state, action) =>
		{
			state.isLoadingAnalyses = false;
			state.singleSellerAnalysis = action.payload as ISellerAnalysisResponse;
		});
		builder.addCase(getSellerAnalysis.rejected, (state, action) =>
		{
			state.isLoadingAnalyses = false;
			state.isError = true;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the Seller Analysis. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the Seller Analysis. Please refresh and try again.';
			}

		});

		// Single Grower Analyses
		builder.addCase(getGrowerAnalysis.pending, (state, action) =>
		{
			state.isLoadingAnalyses = true;
		});
		builder.addCase(getGrowerAnalysis.fulfilled, (state, action) =>
		{
			state.isLoadingAnalyses = false;
			state.singleGrowerAnalysis = action.payload as IGrowerAnalysisResponse;
		});
		builder.addCase(getGrowerAnalysis.rejected, (state, action) =>
		{
			state.isLoadingAnalyses = false;
			state.isError = true;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the Grower Analysis. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the Grower Analysis. Please refresh and try again.';
			}

		});

		// Concern Feedback Post
		builder.addCase(sendFeedbackForConcern.pending, (state, action) =>
		{
			state.isLoadingFeedback = true;
		});
		builder.addCase(sendFeedbackForConcern.fulfilled, (state, action) =>
		{
			state.isLoadingFeedback = false;
		});
		builder.addCase(sendFeedbackForConcern.rejected, (state, action) =>
		{
			state.isLoadingFeedback = false;
			state.isError = true;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to send the feedback. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem sending the feedback. Please refresh and try again.';
			}

		});
	},
});

export const {
	clearState,
	clearError,
	clearSelectedTerritoryAnalysis,
	setSelectedTerritoryId,
	clearSelectedGrowerAnalysis,
	clearSelectedSellerAnalysis,
	clearSelectedSalesRepAnalysis,
	setSelectedSalesRepId,
	setSelectedSellerId,
	setSelectedGrowerId
} = insightsSlice.actions;